import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEO from '../../SEO';
import { cn } from '../../../lib/utils';
import {
  getTournamentById,
  getTournamentBySlug,
  subscribeTournament,
} from '../../../database/tournaments';
import { subscribePlayers } from '../../../database/tournamentPlayers';
import { subscribeStages } from '../../../database/tournamentStages';
import { subscribeVotes } from '../../../database/tournamentVotes';
import {
  focusStageId,
  formatPoints,
  hasVoteCategory,
  isSectionVisible,
  leaderboardRows,
  voteAverages,
  withCommunityVotes,
} from '../../../lib/stages';
import BracketView from './BracketView';

// ponytail: fixed 10 rows per column; past 30 players the columns wrap off a
// 1080p source. Add a ?rows= param when a bigger stage needs to fit.
const ROWS_PER_COLUMN = 10;

// Diagonal edges from the overlay design. Both clips use the same 1.85rem
// run so the rank cell and the row meet on one line.
const RANK_CLIP = '[clip-path:polygon(0_0,100%_0,calc(100%_-_1.85rem)_100%,0_100%)]';
const ROW_CLIP = '[clip-path:polygon(1.85rem_0,100%_0,100%_100%,0_100%)]';

const Notice = ({ children }) => (
  <div className="p-8">
    <span className="rounded-md bg-background/80 px-4 py-2 text-lg text-foreground">
      {children}
    </span>
  </div>
);

const Heading = ({ children }) => (
  <h1 className="text-center font-antebas text-[8rem] font-black uppercase leading-none tracking-[-0.02em] text-foreground">
    {children}
  </h1>
);

const LeaderboardOverlay = ({ stage, playersById, votes, votesHidden }) => {
  const ranked = votes ? withCommunityVotes(stage, votes) : stage;
  const rows = leaderboardRows(ranked, votesHidden);
  const columns = [];
  for (let i = 0; i < rows.length; i += ROWS_PER_COLUMN) {
    columns.push(rows.slice(i, i + ROWS_PER_COLUMN));
  }

  return (
    <div className="flex flex-wrap justify-center gap-[1.8rem]">
      {columns.map((column, c) => (
        <div key={c} className="w-[56.3rem] border-[0.2rem] border-background bg-background">
          <div className="flex h-[4.3rem] items-center justify-end pr-[1.2rem] font-antebas text-[2.1rem] font-black italic uppercase text-foreground">
            Points
          </div>
          {column.map((row, i) => {
            const player = playersById[row.playerId];
            const rank = votesHidden ? null : row.rank;
            return (
              <div key={row.playerId} className="flex h-[4.3rem]">
                <div
                  className={cn(
                    'flex w-[6.25rem] shrink-0 items-center justify-center pr-[1.85rem] font-condensed text-[3.7rem] font-bold leading-none text-foreground',
                    rank === 1 && cn('bg-heat text-heat-foreground', RANK_CLIP)
                  )}
                >
                  {rank ?? '—'}
                </div>
                <div
                  className={cn(
                    '-ml-[1.85rem] flex min-w-0 flex-1 items-center bg-foreground pl-[3.5rem] text-background',
                    ROW_CLIP,
                    i < column.length - 1 && 'border-b border-muted-foreground/60'
                  )}
                >
                  <span className="w-[11.2rem] shrink-0 truncate font-condensed text-[2.3rem] font-semibold leading-none">
                    {player?.name || 'Removed player'}
                  </span>
                  {player?.country && (
                    <img
                      src={`https://flagcdn.com/h40/${player.country.toLowerCase()}.png`}
                      alt=""
                      className="h-[2.25rem] w-[3.45rem] shrink-0 object-cover"
                    />
                  )}
                  <span className="relative ml-auto flex h-full w-[10rem] shrink-0 items-center justify-end pr-[2.6rem] font-antebas text-[2.8rem] italic leading-none">
                    <span className="absolute inset-y-0 left-0 w-[0.1rem] -skew-x-[22deg] bg-muted-foreground/70" />
                    {rank === null ? '—' : formatPoints(row.total)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Transparent page for streaming software (e.g. an OBS browser source at
// 1920x1080). It shows the running stage and follows the tournament as it
// moves on. ?stage=<id> pins one stage, ?title= replaces the heading.
const StageOverlay = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();

  const [tournamentId, setTournamentId] = useState(null);
  const [tournament, setTournament] = useState(undefined);
  const [players, setPlayers] = useState([]);
  const [stages, setStages] = useState(null);
  const [voteDocs, setVoteDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const { documentElement: html, body } = document;
    const before = [html.style.cssText, body.style.cssText];
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    // 1rem = 16px on a 1920x1080 source, so the layout scales with the source.
    html.style.fontSize = 'min(0.8333vw, 1.4815vh)';
    return () => {
      html.style.cssText = before[0];
      body.style.cssText = before[1];
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const found = (await getTournamentBySlug(slug)) || (await getTournamentById(slug));
        if (cancelled) return;
        if (found) setTournamentId(found.id);
        else setTournament(null);
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!tournamentId) return undefined;
    const unsubscribers = [
      subscribeTournament(tournamentId, setTournament, setError),
      subscribePlayers(tournamentId, setPlayers, setError),
      subscribeStages(tournamentId, setStages, setError),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [tournamentId]);

  const votesVisible = tournament?.communityAverageVisible === true;
  const hasVotes = (stages || []).some(hasVoteCategory);
  useEffect(() => {
    if (!(tournamentId && votesVisible && hasVotes)) return undefined;
    const unsubscribe = subscribeVotes(tournamentId, setVoteDocs, () => setVoteDocs(null));
    return () => {
      unsubscribe();
      setVoteDocs(null);
    };
  }, [tournamentId, votesVisible, hasVotes]);

  const playersById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);

  const renderBody = () => {
    if (error) return <Notice>Could not load the overlay.</Notice>;
    if (tournament === null) return <Notice>Tournament not found.</Notice>;
    if (tournament === undefined || stages === null) return null;
    if (!isSectionVisible(tournament, 'stages')) {
      return <Notice>This tournament's stages are not public yet.</Notice>;
    }

    const stage =
      stages.find((s) => s.id === params.get('stage')) ||
      stages.find((s) => s.id === focusStageId(stages));
    if (!stage) return <Notice>No stages yet.</Notice>;

    const index = stages.indexOf(stage);
    const prev = stages[index - 1];
    const votesHidden = hasVoteCategory(stage) && !votesVisible;

    let content;
    if (stage.type === 'bracket') {
      content = (
        <BracketView
          stage={stage}
          playersById={playersById}
          placeholderCount={index === 0 ? players.length : stage.advanceCount || 0}
          placeholderLabel={prev ? (n) => `${prev.name} #${n}` : undefined}
        />
      );
    } else if ((stage.participants || []).length === 0) {
      content = <Notice>Standings appear once this stage starts.</Notice>;
    } else {
      content = (
        <LeaderboardOverlay
          stage={stage}
          playersById={playersById}
          votes={voteDocs ? voteAverages(voteDocs, stage.id) : null}
          votesHidden={votesHidden}
        />
      );
    }

    return (
      <div className="flex flex-col items-center gap-[4rem] px-[2.8rem] py-[3rem]">
        <Heading>{params.get('title') || stage.name}</Heading>
        {content}
      </div>
    );
  };

  return (
    <div className="dark min-h-screen overflow-hidden bg-transparent">
      <SEO title="Stream overlay" noindex />
      {renderBody()}
    </div>
  );
};

export default StageOverlay;
